import React, { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Camera } from "lucide-react";

import { monitoringService } from "../../../api/monitoringService";
import { formatBytes, formatTime } from "../utils/format";

/**
 * Screenshot gallery with a lightbox.
 *
 * Each image needs its own short-lived signed token because an <img src> cannot
 * send an Authorization header — the same reason the video player works this way.
 * Tokens are minted lazily per thumbnail and cached, so opening a session with 100
 * stills does not fire 100 mint requests up front.
 *
 * Note every mint is audited server-side, which is intentional: viewing an
 * employee's screen should leave a trace whether it is a still or a clip.
 */
const ScreenshotGallery = ({ screenshots }) => {
  const [urls, setUrls] = useState({});
  const [lightbox, setLightbox] = useState(null); // index or null
  const [failed, setFailed] = useState({});

  const ensureUrl = useCallback(
    async (shot) => {
      if (!shot?._id || urls[shot._id] || failed[shot._id]) return;
      try {
        const { token } = await monitoringService.createScreenshotToken(shot._id);
        setUrls((prev) => ({
          ...prev,
          [shot._id]: monitoringService.getScreenshotUrl(shot._id, token),
        }));
      } catch {
        setFailed((prev) => ({ ...prev, [shot._id]: true }));
      }
    },
    [urls, failed],
  );

  // Mint for the first screenful eagerly; the rest resolve as they scroll in.
  useEffect(() => {
    (screenshots || []).slice(0, 12).forEach((s) => void ensureUrl(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenshots]);

  const move = useCallback(
    (delta) => {
      setLightbox((cur) => {
        if (cur === null) return cur;
        const next = cur + delta;
        if (next < 0 || next >= screenshots.length) return cur;
        void ensureUrl(screenshots[next]);
        return next;
      });
    },
    [screenshots, ensureUrl],
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, move]);

  if (!screenshots || screenshots.length === 0) {
    return (
      <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-8 text-center">
        <Camera className="w-6 h-6 mx-auto text-gray-300 dark:text-dark-200 mb-2" />
        <p className="text-sm text-gray-500 dark:text-light-500">
          No screenshots for this session
        </p>
      </div>
    );
  }

  const current = lightbox !== null ? screenshots[lightbox] : null;

  return (
    <>
      <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-100 flex items-center gap-2">
          <Camera className="w-4 h-4 text-primary-accent" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100">
            Screenshots
          </h2>
          <span className="text-xs text-gray-400 dark:text-light-500">
            {screenshots.length}
          </span>
        </div>

        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {screenshots.map((shot, index) => (
            <button
              key={shot._id}
              onClick={() => {
                void ensureUrl(shot);
                setLightbox(index);
              }}
              onMouseEnter={() => void ensureUrl(shot)}
              className="group relative aspect-video rounded-lg overflow-hidden bg-gray-100 dark:bg-dark-200 border border-gray-200 dark:border-dark-100 hover:border-primary-accent transition-colors"
            >
              {urls[shot._id] ? (
                <img
                  src={urls[shot._id]}
                  alt={`Screen at ${formatTime(shot.capturedAt)}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  {failed[shot._id] ? (
                    <span className="text-xs text-red-500">unavailable</span>
                  ) : (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-accent" />
                  )}
                </div>
              )}
              <span className="absolute bottom-0 left-0 right-0 px-2 py-1 text-[11px] text-white bg-black/60 flex items-center justify-between">
                <span className="tabular-nums">
                  {formatTime(shot.capturedAt)}
                </span>
                <span>{formatBytes(shot.size)}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Lightbox ── */}
      {current && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              move(-1);
            }}
            disabled={lightbox === 0}
            className="absolute left-2 sm:left-6 p-3 text-white/70 hover:text-white disabled:opacity-20"
            aria-label="Previous"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          <div
            className="max-w-full max-h-full flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            {urls[current._id] ? (
              <img
                src={urls[current._id]}
                alt=""
                className="max-h-[80vh] max-w-full object-contain rounded-lg"
              />
            ) : (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-accent" />
            )}
            <p className="text-sm text-white/80 tabular-nums">
              {formatTime(current.capturedAt)} · {current.width}×
              {current.height} · {formatBytes(current.size)} ·{" "}
              {lightbox + 1}/{screenshots.length}
            </p>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              move(1);
            }}
            disabled={lightbox >= screenshots.length - 1}
            className="absolute right-2 sm:right-6 p-3 text-white/70 hover:text-white disabled:opacity-20"
            aria-label="Next"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </div>
      )}
    </>
  );
};

export default ScreenshotGallery;
